import React, {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import { Button, Snackbar, Typography } from '@material-ui/core';

import { WorkerQueueProvider, useWorkerQueue } from './worker-queue-context';

export type UploadWorkItem<T> = () => Promise<T>;

interface FileUploadContextProps {
  current: number;
  total: number;
  enqueueUpload: <T>(work: UploadWorkItem<T>) => Promise<T>;
  cancelUploads: () => void;
}

const FileUploadContext = createContext<FileUploadContextProps | undefined>(
  undefined
);
FileUploadContext.displayName = 'FileUploadContext';

export const FileUploadProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  return (
    <WorkerQueueProvider>
      <FileUploadQueue>{children}</FileUploadQueue>
    </WorkerQueueProvider>
  );
};

export const useFileUpload = (): FileUploadContextProps => {
  const context = useContext(FileUploadContext);

  if (!context) {
    throw new Error('useFileUpload must be used within a FileUploadProvider');
  }

  return context;
};

const FileUploadQueue: FC<{ children: ReactNode }> = ({ children }) => {
  const { cancel, current, enqueue, total } = useWorkerQueue();

  // Warn user if they try to leave the page while uploads are in progress
  useEffect(() => {
    if (total === 0) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [total]);

  const value = useMemo(
    () => ({
      current,
      total,
      enqueueUpload: enqueue,
      cancelUploads: cancel,
    }),
    [cancel, current, enqueue, total]
  );

  return (
    <FileUploadContext.Provider value={value}>
      {children}
      <UploadProgress total={total} current={current} onCancel={cancel} />
    </FileUploadContext.Provider>
  );
};

interface UploadProgressProps {
  total: number;
  current: number;
  onCancel: () => void;
}

const UploadProgress: FC<UploadProgressProps> = ({
  total,
  current,
  onCancel,
}) => {
  return (
    <Snackbar
      open={total > 0}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      style={{ top: 75 }}
      message={
        <div>
          <Typography variant='body1'>
            Uppladdning pågår: {current}/{total}
          </Typography>
        </div>
      }
      action={
        <Button color='inherit' size='small' onClick={onCancel}>
          Avbryt
        </Button>
      }
    />
  );
};
